import React, { useState, Suspense } from 'react';
import { 
  Container, 
  TextField, 
  Button, 
  Typography, 
  Box, 
  CircularProgress, 
  Paper,
  Grid,
  Tabs,
  Tab,
  Alert,
  IconButton,
  Tooltip,
  FormGroup,
  FormControlLabel,
  Switch,
  Slider,
  LinearProgress
} from '@mui/material';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Environment, useGLTF } from '@react-three/drei';
import { create3DModel, pollFor3DModel } from '../services/hunyuan3dService';
import SettingsIcon from '@mui/icons-material/Settings';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

// 3D Model Viewer Component
const ModelViewer = ({ modelUrl }) => {
  // Move useGLTF to the top level and handle null case
  const { scene } = useGLTF(modelUrl || '') || {};
  
  if (!modelUrl || !scene) return null;
  
  return (
    <primitive 
      object={scene} 
      scale={0.5} 
      position={[0, 0, 0]}
    />
  );
};

const Model3DCreationPage = () => {
  // Input state
  const [prompt, setPrompt] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [isImageMode, setIsImageMode] = useState(false);
  
  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [generatedModels, setGeneratedModels] = useState([]);
  const [progress, setProgress] = useState(0);
  const [predictionId, setPredictionId] = useState('');
  
  // Settings state
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [settings, setSettings] = useState({
    guidance_scale: 5.5,
    octree_resolution: 256,
    inference_steps: 20,
    remove_background: false,
    seed: -1
  });
  
  // Add state for the currently viewed 3D model
  const [viewingModel, setViewingModel] = useState(null);
  
  // Handle settings change
  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };
  
  // Toggle advanced settings
  const toggleAdvancedSettings = () => {
    setShowAdvanced(prev => !prev);
  };

  const handleGenerate = async () => {
    if (!prompt.trim() && !isImageMode) {
      setError('Please enter a prompt or upload an image');
      return;
    }

    setError('');
    setIsGenerating(true);
    setProgress(0);
    setViewingModel(null); // Reset viewing model

    try {
      const generationOptions = {
        prompt: isImageMode ? '' : prompt,
        image: isImageMode ? imageUrl : undefined,
        ...settings
      };
      
      console.log('Generation options:', generationOptions);
      
      // Start the 3D model generation
      const prediction = await create3DModel(generationOptions);
      setPredictionId(prediction.id);
      
      // Poll for completion
      const result = await pollFor3DModel(prediction.id);
      
      // Add to generated models
      const newModel = {
        id: prediction.id,
        prompt: isImageMode ? 'Image to 3D' : prompt,
        date: new Date().toISOString(),
        status: 'completed',
        modelUrl: result.output?.mesh,
        thumbnailUrl: result.output?.images?.[0] || '',
        settings: { ...settings }
      };
      
      setGeneratedModels(prev => [newModel, ...prev]);
      setViewingModel(newModel); // Set the newly generated model for viewing
      setProgress(100);
      
      // Reset form
      if (!isImageMode) setPrompt('');
      setImageUrl('');
    } catch (err) {
      console.error('Error generating 3D model:', err);
      setError(err.message || 'Failed to generate 3D model');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    const reader = new FileReader();
    reader.onload = () => {
      setImageUrl(reader.result);
      setIsImageMode(true);
    };
    reader.readAsDataURL(file);
  };

  const handleClearImage = () => {
    setImageUrl('');
    setIsImageMode(false);
  };

  // Function to view a specific model
  const handleViewModel = (model) => {
    setViewingModel(model);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          3D Model Generation
        </Typography>
        <Box>
          <Button 
            variant="outlined" 
            startIcon={<SettingsIcon />}
            onClick={toggleAdvancedSettings}
            endIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          >
            {showAdvanced ? 'Hide' : 'Show'} Advanced Settings
          </Button>
        </Box>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Grid container spacing={3}>
        {/* Left side - Input and settings */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, mb: 3, height: '100%' }}>
            {/* Main Input Section */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={showAdvanced ? 8 : 12}>
                {isImageMode ? (
                  <Box sx={{ mb: 2, position: 'relative' }}>
                    {imageUrl && (
                      <Box 
                        sx={{ 
                          border: '1px dashed #ccc', 
                          borderRadius: 1, 
                          p: 1, 
                          mb: 2,
                          position: 'relative',
                          minHeight: '150px',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          bgcolor: 'rgba(0,0,0,0.02)'
                        }}
                      >
                        <img 
                          src={imageUrl} 
                          alt="Uploaded for 3D generation" 
                          style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain' }} 
                        />
                        <IconButton 
                          onClick={handleClearImage}
                          size="small"
                          sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'background.paper' }}
                        >
                          ✕
                        </IconButton>
                      </Box>
                    )}
                    <Button
                      variant="outlined"
                      component="label"
                      fullWidth
                    >
                      {imageUrl ? 'Change Image' : 'Upload Image'}
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleImageUpload}
                      />
                    </Button>
                    <Typography variant="caption" display="block" sx={{ mt: 1, textAlign: 'center' }}>
                      Or enter a text prompt below
                    </Typography>
                  </Box>
                ) : (
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    sx={{ mb: 2 }}
                  >
                    Upload Image (Optional)
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleImageUpload}
                    />
                  </Button>
                )}
                
                <TextField
                  fullWidth
                  variant="outlined"
                  label={isImageMode ? "Additional guidance (optional)" : "Describe the 3D model you want to create"}
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  disabled={isGenerating}
                  multiline
                  rows={isImageMode ? 2 : 4}
                  placeholder={isImageMode ? "E.g., 'A red sports car with black rims'" : "E.g., 'A realistic 3D model of a futuristic sports car'"}
                />
              </Grid>
              
              {/* Advanced Settings */}
              {showAdvanced && (
                <Grid item xs={12} md={4}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Generation Settings
                    </Typography>
                    
                    <FormGroup sx={{ mb: 2 }}>
                      <FormControlLabel 
                        control={
                          <Switch 
                            checked={settings.remove_background} 
                            onChange={(e) => handleSettingChange('remove_background', e.target.checked)}
                            size="small"
                          />
                        } 
                        label="Remove Background" 
                      />
                    </FormGroup>
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography id="guidance-scale-slider" variant="caption" display="block" gutterBottom>
                        Guidance Scale: {settings.guidance_scale}
                      </Typography>
                      <Slider
                        value={settings.guidance_scale}
                        onChange={(e, value) => handleSettingChange('guidance_scale', value)}
                        aria-labelledby="guidance-scale-slider"
                        min={1}
                        max={20}
                        step={0.1}
                        valueLabelDisplay="auto"
                        disabled={isGenerating}
                      />
                    </Box>
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography id="octree-resolution-slider" variant="caption" display="block" gutterBottom>
                        Octree Resolution: {settings.octree_resolution}
                      </Typography>
                      <Slider
                        value={settings.octree_resolution}
                        onChange={(e, value) => handleSettingChange('octree_resolution', value)}
                        aria-labelledby="octree-resolution-slider"
                        min={128}
                        max={512}
                        step={32}
                        valueLabelDisplay="auto"
                        disabled={isGenerating}
                      />
                    </Box>
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography id="inference-steps-slider" variant="caption" display="block" gutterBottom>
                        Inference Steps: {settings.inference_steps}
                      </Typography>
                      <Slider
                        value={settings.inference_steps}
                        onChange={(e, value) => handleSettingChange('inference_steps', value)}
                        aria-labelledby="inference-steps-slider"
                        min={10}
                        max={50}
                        step={1}
                        valueLabelDisplay="auto"
                        disabled={isGenerating}
                      />
                    </Box>
                    
                    <TextField
                      label="Seed"
                      type="number"
                      value={settings.seed === -1 ? '' : settings.seed}
                      onChange={(e) => handleSettingChange('seed', e.target.value === '' ? -1 : parseInt(e.target.value))}
                      fullWidth
                      size="small"
                      margin="dense"
                      helperText="Leave empty for random"
                      disabled={isGenerating}
                    />
                  </Paper>
                </Grid>
              )}
            </Grid>
            
            {/* Generate Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleGenerate}
                disabled={isGenerating || (!prompt.trim() && !isImageMode)}
                startIcon={isGenerating ? <CircularProgress size={20} color="inherit" /> : null}
                size="large"
              >
                {isGenerating ? 'Generating...' : 'Generate 3D Model'}
              </Button>
            </Box>
            
            {/* Progress Indicator */}
            {isGenerating && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Generating 3D model... This may take a few minutes.
                  {predictionId && (
                    <Box component="span" sx={{ ml: 1, fontFamily: 'monospace' }}>
                      ID: {predictionId}
                    </Box>
                  )}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: '100%', mr: 1 }}>
                    <LinearProgress variant={progress > 0 ? 'determinate' : 'indeterminate'} value={progress} />
                  </Box>
                  {progress > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      {Math.round(progress)}%
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>
        
        {/* Right side - 3D Preview */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '500px', position: 'relative' }}>
            {viewingModel ? (
              <Canvas camera={{ position: [0, 0, 5], fov: 50 }}>
                <ambientLight intensity={0.5} />
                <spotLight position={[10, 10, 10]} angle={0.15} penumbra={1} />
                <pointLight position={[-10, -10, -10]} />
                <Suspense fallback={null}>
                  <ModelViewer modelUrl={viewingModel.modelUrl} />
                </Suspense>
                <OrbitControls />
                <Environment preset="city" />
              </Canvas>
            ) : (
              <Box 
                sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  height: '100%',
                  border: '2px dashed #ccc',
                  borderRadius: 1,
                  bgcolor: 'rgba(0,0,0,0.02)'
                }}
              >
                {isGenerating ? (
                  <>
                    <CircularProgress size={60} thickness={4} />
                    <Typography variant="h6" sx={{ mt: 2 }}>Generating 3D Model...</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      This may take a few moments
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="h6" color="text.secondary">
                      3D Model Preview
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center', maxWidth: '80%' }}>
                      {generatedModels.length > 0 
                        ? 'Select a model from the list below to view it here'
                        : 'Generate a 3D model to see it appear here'}
                    </Typography>
                  </>
                )}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Results Section */}
      <Paper sx={{ p: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          sx={{ mb: 3 }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Generated Models" />
          <Tab label="Templates" />
        </Tabs>
        
        {activeTab === 0 && (
          <Grid container spacing={3}>
            {generatedModels.length > 0 ? (
              generatedModels.map((model) => (
                <Grid item xs={12} sm={6} md={4} key={model.id}>
                  <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {model.thumbnailUrl && (
                      <Box 
                        sx={{ 
                          border: '1px dashed #ccc', 
                          borderRadius: 1, 
                          p: 1, 
                          mb: 2,
                          position: 'relative',
                          minHeight: '150px',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          bgcolor: 'rgba(0,0,0,0.02)'
                        }}
                      >
                        <img 
                          src={model.thumbnailUrl} 
                          alt={model.prompt} 
                          style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain' }} 
                        />
                      </Box>
                    )}
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1" gutterBottom noWrap>
                        {model.prompt || '3D Model'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {new Date(model.date).toLocaleString()}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                        {model.modelUrl ? (
                          <Button 
                            variant="outlined" 
                            color="primary" 
                            href={model.modelUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                          >
                            Download
                          </Button>
                        ) : (
                          <Typography variant="body2" color="warning.main">
                            Processing...
                          </Typography>
                        )}
                        <Tooltip title="View settings used for this generation">
                          <IconButton size="small">
                            <SettingsIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Button 
                          variant="outlined" 
                          size="small" 
                          onClick={() => handleViewModel(model)}
                          sx={{ mt: 1 }}
                        >
                          View in 3D
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No models generated yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {isImageMode 
                      ? 'Upload an image and click "Generate 3D Model" to get started.'
                      : 'Enter a prompt and click "Generate 3D Model" to get started.'}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        )}
        
        {activeTab === 1 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Coming Soon
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Browse and use pre-made 3D model templates
            </Typography>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default Model3DCreationPage;
